import { AlertTriangle, Download, Globe, Link2, Lightbulb, Plus, RefreshCw, Search, ShieldCheck, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { LINK_KIND_LABELS, LINK_KINDS, type LinkKind } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { fmt, productCode, when } from "../../lib/format";
import { keys, useAction, useLinkInsights, useLinks, useMe } from "../../lib/queries";
import type { LinkRow, LinksState } from "../../lib/types";
import { Badge, type Tone } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { EmptyState, ErrorState, PageSkeleton, useToast } from "../../ui/Feedback";
import { ChipsInput, Field, FormGrid, Input, Select } from "../../ui/Form";
import table from "../../ui/Table.module.css";
import { LinkEditor, type LinkDraft } from "./LinkEditor";
import { LinkImportDialog } from "./LinkImportDialog";
import { useProjectContext } from "./ProjectLayout";
import { SitemapDialog } from "./SitemapDialog";
import styles from "./LinksTab.module.css";

const STATUS: Record<LinkRow["status"], { tone: Tone; label: string }> = {
  ok: { tone: "good", label: "Aktif di web" },
  unchecked: { tone: "neutral", label: "Belum dicek" },
  broken: { tone: "critical", label: "Rusak" },
  section_missing: { tone: "warning", label: "Bagian hilang" },
  error: { tone: "warning", label: "Gagal dicek" },
};

const SOURCE_LABEL: Record<LinkRow["source"], string> = { manual: "Manual", import: "Impor", sitemap: "Sitemap", feed: "Feed", api: "API / panel admin" };

/**
 * Linkage (catalog v4, sheet AI Chatbot G): the places on the client's
 * website the chatbot may send a visitor. The list is live, not versioned:
 * a change here applies to the very next answer.
 */
export function LinksTab() {
  const { project } = useProjectContext();
  const me = useMe().data!;
  const links = useLinks(project.id);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<LinkKind | "all">("all");
  const [status, setStatus] = useState<"all" | "active" | "inactive" | "problem">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<LinkDraft | null>(null);
  const [importing, setImporting] = useState(false);
  const [sitemap, setSitemap] = useState(false);
  const toast = useToast();

  const invalidate = [keys.links(project.id), keys.project(project.id)];
  const bulk = useAction((body: { action: string; ids: string[] }) => api.post<{ count?: number; checked?: number; broken?: number }>(`/projects/${project.id}/links/bulk`, body), invalidate);

  const rows = links.data?.links;
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (rows ?? []).filter(
      (link) =>
        (kind === "all" || link.kind === kind) &&
        (status === "all" ||
          (status === "active" && link.active) ||
          (status === "inactive" && !link.active) ||
          (status === "problem" && (link.status === "broken" || link.status === "section_missing"))) &&
        (!needle || `${link.title} ${link.summary} ${link.url} ${link.tags.join(" ")}`.toLowerCase().includes(needle)),
    );
  }, [rows, query, kind, status]);

  if (links.isPending) return <PageSkeleton />;
  if (links.isError) return <ErrorState error={links.error} />;
  const state = links.data;
  const active = state.links.filter((link) => link.active).length;
  const problems = state.links.filter((link) => link.status === "broken" || link.status === "section_missing").length;
  const staff = me.role !== "client";
  const ids = [...selected].filter((id) => filtered.some((link) => link.id === id));

  async function run(action: "activate" | "deactivate" | "delete" | "check", target: string[]) {
    if (action === "delete" && !confirm(`Hapus ${target.length} rujukan? Riwayat kliknya tetap ada di laporan.`)) return;
    try {
      const result = await bulk.mutateAsync({ action, ids: target });
      toast(
        action === "check"
          ? `${result.checked} tautan dicek${result.broken ? `, ${result.broken} rusak` : ", semuanya baik"}.`
          : { activate: "Rujukan diaktifkan.", deactivate: "Rujukan dinonaktifkan.", delete: "Rujukan dihapus." }[action],
      );
      setSelected(new Set());
    } catch (error) {
      toast(error instanceof Error ? error.message : "Gagal.", "critical");
    }
  }

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <div className={styles.status}>
          <div className={styles.statusText}>
            <div className={styles.statusTitle}>
              {state.enabled ? <Badge tone="good" icon={<ShieldCheck aria-hidden />}>Linkage aktif</Badge> : <Badge tone="warning">Belum termasuk Linkage</Badge>}
              <span>
                {fmt.int(active)} rujukan aktif dari {fmt.int(state.links.length)}
                {problems > 0 && <span className={styles.problem}> · {problems} bermasalah</span>}
              </span>
            </div>
            <p className={styles.statusNote}>
              {state.enabled
                ? "Chatbot menyarankan sampai 3 rujukan di bawah jawaban, hanya dari daftar ini. Perubahan langsung berlaku, tanpa diterbitkan."
                : `Produk ${productCode(project.product)} tidak memakai Linkage, jadi jawaban belum membawa rujukan. Daftar ini bisa disiapkan dulu; ubah produk ke versi berakhiran L di Pengaturan untuk menyalakannya.`}
            </p>
          </div>
          <div className={styles.statusActions}>
            <Button icon={<Upload aria-hidden />} onClick={() => setImporting(true)}>
              Impor
            </Button>
            <Button icon={<Globe aria-hidden />} onClick={() => setSitemap(true)}>
              Dari sitemap
            </Button>
            <a className={styles.exportLink} href={`/admin/v1/projects/${project.id}/links/export`} download>
              <Download aria-hidden /> Ekspor
            </a>
            <Button variant="primary" icon={<Plus aria-hidden />} onClick={() => setEditing(blankLink())}>
              Rujukan baru
            </Button>
          </div>
        </div>

        <div className={styles.filters}>
          <label className={styles.search}>
            <Search aria-hidden />
            <input placeholder="Cari judul, alamat, atau tag…" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Cari rujukan" />
          </label>
          <Select value={kind} onChange={(event) => setKind(event.target.value as LinkKind | "all")} aria-label="Jenis" className={styles.select}>
            <option value="all">Semua jenis</option>
            {LINK_KINDS.map((k) => (
              <option key={k} value={k}>
                {LINK_KIND_LABELS[k]} ({state.links.filter((link) => link.kind === k).length})
              </option>
            ))}
          </Select>
          <Select value={status} onChange={(event) => setStatus(event.target.value as typeof status)} aria-label="Status" className={styles.select}>
            <option value="all">Semua status</option>
            <option value="active">Aktif</option>
            <option value="inactive">Nonaktif</option>
            <option value="problem">Bermasalah</option>
          </Select>
        </div>

        {ids.length > 0 && (
          <div className={styles.bulk} role="toolbar" aria-label="Aksi untuk rujukan terpilih">
            <strong>{ids.length} dipilih</strong>
            <Button size="sm" onClick={() => run("activate", ids)}>
              Aktifkan
            </Button>
            <Button size="sm" onClick={() => run("deactivate", ids)}>
              Nonaktifkan
            </Button>
            <Button size="sm" icon={<RefreshCw aria-hidden />} disabled={ids.length > 20} title={ids.length > 20 ? "Paling banyak 20 sekaligus" : undefined} onClick={() => run("check", ids)}>
              Cek tautan
            </Button>
            <Button size="sm" variant="danger" onClick={() => run("delete", ids)}>
              Hapus
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Batal
            </Button>
          </div>
        )}

        <Card flush>
          {filtered.length === 0 ? (
            <EmptyState icon={<Link2 aria-hidden />} title={state.links.length ? "Tidak ada yang cocok" : "Belum ada rujukan"}>
              {state.links.length
                ? "Ubah kata kunci atau saringannya."
                : "Website Navibiz: impor file knowledge.json dari npm run kb:export. Website luar: mulai dari sitemap, sampai 100 rujukan."}
            </EmptyState>
          ) : (
            <div className={table.wrap} style={{ opacity: bulk.isPending ? 0.6 : 1 }}>
              <table className={table.table}>
                <thead>
                  <tr>
                    <th className={table.first} style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        aria-label="Pilih semua"
                        checked={filtered.length > 0 && filtered.every((link) => selected.has(link.id))}
                        onChange={(event) => setSelected(event.target.checked ? new Set(filtered.map((link) => link.id)) : new Set())}
                      />
                    </th>
                    <th>Rujukan</th>
                    <th>Jenis</th>
                    <th>Status</th>
                    <th className={table.right} title="30 hari terakhir: berapa kali disarankan, berapa kali diklik">
                      Klik / tawar
                    </th>
                    <th className={table.last} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((link) => (
                    <tr key={link.id} style={{ opacity: link.active ? 1 : 0.55 }}>
                      <td className={table.first}>
                        <input
                          type="checkbox"
                          aria-label={`Pilih ${link.title}`}
                          checked={selected.has(link.id)}
                          onChange={(event) => {
                            const next = new Set(selected);
                            if (event.target.checked) next.add(link.id);
                            else next.delete(link.id);
                            setSelected(next);
                          }}
                        />
                      </td>
                      <td className={styles.linkCell}>
                        <div className={styles.linkTitle}>
                          {link.title}
                          {link.price && <span className={styles.price}>{link.price}</span>}
                        </div>
                        <a className={styles.url} href={link.url} target="_blank" rel="noreferrer">
                          {link.url.replace(/^https?:\/\//, "")}
                        </a>
                        {link.summary && <div className={styles.summary}>{link.summary}</div>}
                      </td>
                      <td>
                        <Badge tone={link.kind === "section" ? "accent" : "neutral"}>{LINK_KIND_LABELS[link.kind]}</Badge>
                        <div className={table.muted}>{SOURCE_LABEL[link.source]}</div>
                      </td>
                      <td>
                        <Badge tone={link.active ? STATUS[link.status].tone : "neutral"}>{link.active ? STATUS[link.status].label : "Nonaktif"}</Badge>
                        <div className={table.muted} title={link.statusDetail ?? undefined}>
                          {link.statusDetail ?? (link.checkedAt ? `dicek ${when.ago(link.checkedAt)}` : "")}
                        </div>
                      </td>
                      <td className={table.right}>
                        <span className="num">
                          {fmt.int(link.clicks)} / {fmt.int(link.offered)}
                        </span>
                        {link.offered > 0 && <div className={table.muted}>{fmt.percent(link.clicks / link.offered)}</div>}
                      </td>
                      <td className={table.last}>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(link)}>
                          Ubah
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <aside className={styles.side}>
        <InsightsPanel projectId={project.id} onAdd={(title) => setEditing({ ...blankLink(), title })} />
        {staff && <SettingsPanel projectId={project.id} state={state} websiteUrl={project.websiteUrl} />}
      </aside>

      <LinkEditor projectId={project.id} websiteUrl={project.websiteUrl} draft={editing} onClose={() => setEditing(null)} />
      <LinkImportDialog projectId={project.id} open={importing} onClose={() => setImporting(false)} />
      <SitemapDialog projectId={project.id} websiteUrl={project.websiteUrl} open={sitemap} onClose={() => setSitemap(false)} />
    </div>
  );
}

function blankLink(): LinkDraft {
  return { kind: "page", title: "", url: "", summary: "", price: null, imageUrl: null, tags: [], active: true };
}

function InsightsPanel({ projectId, onAdd }: { projectId: string; onAdd: (title: string) => void }) {
  const insights = useLinkInsights(projectId, 30);
  const data = insights.data;
  return (
    <Card title="Kinerja rujukan" description="30 hari terakhir, dari widget dan API.">
      {!data ? null : (
        <div className={styles.insights}>
          <div className={styles.kpis}>
            <div>
              <span>Ditawarkan</span>
              <strong>{fmt.int(data.totals.offered)}</strong>
            </div>
            <div>
              <span>Diklik</span>
              <strong>{fmt.int(data.totals.clicks)}</strong>
            </div>
            <div>
              <span>Rasio klik</span>
              <strong>{data.totals.offered ? fmt.percent(data.totals.clicks / data.totals.offered) : "—"}</strong>
            </div>
          </div>

          <h3 className={styles.sideTitle}>Paling sering diklik</h3>
          {data.top.length === 0 ? (
            <p className={styles.muted}>Belum ada klik.</p>
          ) : (
            <ol className={styles.top}>
              {data.top.slice(0, 5).map((row) => (
                <li key={row.id}>
                  <span>{row.title ?? "(rujukan dihapus)"}</span>
                  <span className="num">{fmt.int(row.clicks)}</span>
                </li>
              ))}
            </ol>
          )}

          {data.broken.length > 0 && (
            <>
              <h3 className={styles.sideTitle}>
                <AlertTriangle aria-hidden /> Tautan bermasalah
              </h3>
              <ul className={styles.top}>
                {data.broken.map((row) => (
                  <li key={row.id}>
                    <span>{row.title}</span>
                    <span className={styles.muted}>{row.statusDetail}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3 className={styles.sideTitle}>
            <Lightbulb aria-hidden /> Pertanyaan tanpa rujukan
          </h3>
          {data.unlinked.length === 0 ? (
            <p className={styles.muted}>Setiap pertanyaan yang terjawab mendapat rujukan.</p>
          ) : (
            <ul className={styles.gaps}>
              {data.unlinked.slice(0, 6).map((cluster) => (
                <li key={cluster.label}>
                  <div>
                    <div className={styles.gapLabel}>“{cluster.label}”</div>
                    <div className={styles.muted}>{fmt.int(cluster.count)}× ditanyakan</div>
                  </div>
                  <Button size="sm" icon={<Plus aria-hidden />} onClick={() => onAdd(cluster.label)}>
                    Rujukan
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

const FEED_LABEL = {
  navibiz: "Navibiz (Web Dinamis /linkage.json, atau file kb:export)",
  shopify: "Shopify (alamat toko)",
  woocommerce: "WooCommerce (alamat toko)",
  csv: "CSV (mis. Google Sheets: File → Bagikan → Publikasikan ke web → CSV)",
} as const;

/** Allowed domains and the nightly feed: Navibiz staff only, since both decide what the chatbot may point at. */
function SettingsPanel({ projectId, state, websiteUrl }: { projectId: string; state: LinksState; websiteUrl: string | null }) {
  const toast = useToast();
  const [domains, setDomains] = useState(state.settings.domains);
  const [format, setFormat] = useState<keyof typeof FEED_LABEL | "">(state.settings.feed?.format ?? "");
  const [url, setUrl] = useState(state.settings.feed?.url ?? "");
  const invalidate = [keys.links(projectId), keys.project(projectId)];
  const save = useAction((body: unknown) => api.put(`/projects/${projectId}/links/settings`, body), invalidate);
  const sync = useAction(() => api.post<{ created: number; updated: number; deactivated: number }>(`/projects/${projectId}/links/feed/sync`), invalidate);
  const dirty = JSON.stringify(domains) !== JSON.stringify(state.settings.domains) || format !== (state.settings.feed?.format ?? "") || url !== (state.settings.feed?.url ?? "");

  return (
    <Card title="Domain & sumber otomatis" description="Rujukan hanya boleh menunjuk ke domain ini. Feed disinkron setiap malam.">
      <div className={styles.settings}>
        <Field label="Domain yang diizinkan" hint="Tanpa https://. Pakai *.domain.com untuk semua subdomain.">
          {(id) => <ChipsInput id={id} value={domains} onChange={setDomains} placeholder={websiteUrl ? new URL(websiteUrl).hostname : "toko.biz.id"} />}
        </Field>
        <FormGrid columns={1}>
          <Field label="Sumber otomatis">
            {(id) => (
              <Select id={id} value={format} onChange={(event) => setFormat(event.target.value as typeof format)}>
                <option value="">Tidak ada (kelola manual)</option>
                {Object.entries(FEED_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {format && <Field label="Alamat feed">{(id) => <Input id={id} type="url" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://…" />}</Field>}
        </FormGrid>
        {state.settings.feed && (
          <p className={styles.muted}>
            {state.settings.feedSyncedAt ? `Sinkron terakhir ${when.ago(state.settings.feedSyncedAt)}: ${state.settings.feedResult}` : "Belum pernah disinkron."}
          </p>
        )}
        <div className={styles.settingsActions}>
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty || (!!format && !url)}
            loading={save.isPending}
            onClick={async () => {
              try {
                await save.mutateAsync({ domains, feed: format ? { format, url } : null });
                toast("Pengaturan Linkage disimpan.");
              } catch (error) {
                toast(error instanceof Error ? error.message : "Gagal menyimpan.", "critical");
              }
            }}
          >
            Simpan
          </Button>
          {state.settings.feed && (
            <Button
              size="sm"
              icon={<RefreshCw aria-hidden />}
              loading={sync.isPending}
              disabled={dirty}
              onClick={async () => {
                try {
                  const result = await sync.mutateAsync(undefined);
                  toast(`Sinkron selesai: ${result.created} baru, ${result.updated} diperbarui, ${result.deactivated} dinonaktifkan.`);
                } catch (error) {
                  toast(error instanceof Error ? error.message : "Sinkron gagal.", "critical");
                }
              }}
            >
              Sinkron sekarang
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
